/**
 * The visitor's country from the browser time zone — mirrors Flutter CountryInfoRepo.findMyCountry
 * (lib/domain/core/country_code_repository/country_code_repository.dart): the first country in its
 * table with a matching time zone, India when none matches.
 */

const DEFAULT_COUNTRY = 'India';

/** Flutter CountryInfoRepo.country — time zone → country name (first match wins). */
const COUNTRY_BY_TIME_ZONE: Record<string, string> = {
  'Asia/Calcutta': 'India',
  'Asia/Kuala_Lumpur': 'Malaysia',
  'Asia/Kabul': 'Afghanistan',
  'Europe/Mariehamn': 'Aland Islands',
  'Europe/Tirane': 'Albania',
  'Africa/Algiers': 'Algeria',
  'Pacific/Pago_Pago': 'AmericanSamoa',
  'Europe/Andorra': 'Andorra',
  'Africa/Luanda': 'Angola',
  'America/Anguilla': 'Anguilla',
  'Antarctica/Palmer': 'Antarctica',
  'America/Antigua': 'Antigua and Barbuda',
  'America/Argentina/Buenos_Aires': 'Argentina',
  'Asia/Yerevan': 'Armenia',
  'America/Aruba': 'Aruba',
  'Australia/Sydney': 'Australia',
  'Europe/Vienna': 'Austria',
  'Asia/Baku': 'Azerbaijan',
  'America/Nassau': 'Bahamas',
  'Asia/Bahrain': 'Bahrain',
  'Asia/Dhaka': 'Bangladesh',
  'America/Barbados': 'Barbados',
  'Europe/Minsk': 'Belarus',
  'Europe/Brussels': 'Belgium',
  'America/Belize': 'Belize',
  'Africa/Porto-Novo': 'Benin',
  'Atlantic/Bermuda': 'Bermuda',
  'Asia/Thimphu': 'Bhutan',
  'America/La_Paz': 'Bolivia, Plurinational State of',
  'Europe/Sarajevo': 'Bosnia and Herzegovina',
  'Africa/Gaborone': 'Botswana',
  'America/Sao_Paulo': 'Brazil',
  'Indian/Chagos': 'British Indian Ocean Territory',
  'Asia/Brunei': 'Brunei Darussalam',
  'Europe/Sofia': 'Bulgaria',
  'Africa/Ouagadougou': 'Burkina Faso',
  'Africa/Bujumbura': 'Burundi',
  'Asia/Phnom_Penh': 'Cambodia',
  'Africa/Douala': 'Cameroon',
  'America/Toronto': 'Canada',
  'Atlantic/Cape_Verde': 'Cape Verde',
  'America/Cayman': 'Cayman Islands',
  'Africa/Bangui': 'Central African Republic',
  'Africa/Ndjamena': 'Chad',
  'America/Santiago': 'Chile',
  'Asia/Shanghai': 'China',
  'Indian/Christmas': 'Christmas Island',
  'Indian/Cocos': 'Cocos (Keeling) Islands',
  'America/Bogota': 'Colombia',
  'Indian/Comoro': 'Comoros',
  'Africa/Brazzaville': 'Congo',
  'Africa/Kinshasa': 'Congo, The Democratic Republic of the',
  'Pacific/Rarotonga': 'Cook Islands',
  'America/Costa_Rica': 'Costa Rica',
  'Africa/Abidjan': "Cote d'Ivoire",
  'Europe/Zagreb': 'Croatia',
  'America/Havana': 'Cuba',
  'Asia/Nicosia': 'Cyprus',
  'Europe/Prague': 'Czech Republic',
  'Europe/Copenhagen': 'Denmark',
  'Africa/Djibouti': 'Djibouti',
  'America/Dominica': 'Dominica',
  'America/Santo_Domingo': 'Dominican Republic',
  'America/Guayaquil': 'Ecuador',
  'Africa/Cairo': 'Egypt',
  'America/El_Salvador': 'El Salvador',
  'Africa/Malabo': 'Equatorial Guinea',
  'Africa/Asmara': 'Eritrea',
  'Europe/Tallinn': 'Estonia',
  'Africa/Addis_Ababa': 'Ethiopia',
  'Atlantic/Stanley': 'Falkland Islands (Malvinas)',
  'Atlantic/Faroe': 'Faroe Islands',
  'Pacific/Fiji': 'Fiji',
  'Europe/Helsinki': 'Finland',
  'Europe/Paris': 'France',
  'America/Cayenne': 'French Guiana',
  'Pacific/Tahiti': 'French Polynesia',
  'Africa/Libreville': 'Gabon',
  'Africa/Banjul': 'Gambia',
  'Asia/Tbilisi': 'Georgia',
  'Europe/Berlin': 'Germany',
  'Africa/Accra': 'Ghana',
  'Europe/Gibraltar': 'Gibraltar',
  'Europe/Athens': 'Greece',
  'America/Godthab': 'Greenland',
  'America/Grenada': 'Grenada',
  'America/Guadeloupe': 'Guadeloupe',
  'Pacific/Guam': 'Guam',
  'America/Guatemala': 'Guatemala',
  'Europe/Guernsey': 'Guernsey',
  'Africa/Conakry': 'Guinea',
  'Africa/Bissau': 'Guinea-Bissau',
  'America/Guyana': 'Guyana',
  'America/Port-au-Prince': 'Haiti',
  'Europe/Vatican': 'Holy See (Vatican City State)',
  'America/Tegucigalpa': 'Honduras',
  'Asia/Hong_Kong': 'Hong Kong',
  'Europe/Budapest': 'Hungary',
  'Atlantic/Reykjavik': 'Iceland',
  'Asia/Kolkata': 'India',
  'Asia/Jakarta': 'Indonesia',
  'Asia/Tehran': 'Iran, Islamic Republic of',
  'Asia/Baghdad': 'Iraq',
  'Europe/Dublin': 'Ireland',
  'Europe/Isle_of_Man': 'Isle of Man',
  'Asia/Jerusalem': 'Israel',
  'Europe/Rome': 'Italy',
  'America/Jamaica': 'Jamaica',
  'Asia/Tokyo': 'Japan',
  'Europe/Jersey': 'Jersey',
  'Asia/Amman': 'Jordan',
  'Asia/Almaty': 'Kazakhstan',
  'Africa/Nairobi': 'Kenya',
  'Pacific/Tarawa': 'Kiribati',
  'Asia/Pyongyang': "Korea, Democratic People's Republic of",
  'Asia/Seoul': 'Korea, Republic of',
  'Asia/Kuwait': 'Kuwait',
  'Asia/Bishkek': 'Kyrgyzstan',
  'Asia/Vientiane': "Lao People's Democratic Republic",
  'Europe/Riga': 'Latvia',
  'Asia/Beirut': 'Lebanon',
  'Africa/Maseru': 'Lesotho',
  'Africa/Monrovia': 'Liberia',
  'Africa/Tripoli': 'Libyan Arab Jamahiriya',
  'Europe/Vaduz': 'Liechtenstein',
  'Europe/Vilnius': 'Lithuania',
  'Europe/Luxembourg': 'Luxembourg',
  'Asia/Macau': 'Macao',
  'Europe/Skopje': 'Macedonia, The Former Yugoslav Republic of',
  'Indian/Antananarivo': 'Madagascar',
  'Africa/Blantyre': 'Malawi',
  'Indian/Maldives': 'Maldives',
  'Africa/Bamako': 'Mali',
  'Europe/Malta': 'Malta',
  'Pacific/Majuro': 'Marshall Islands',
  'America/Martinique': 'Martinique',
  'Africa/Nouakchott': 'Mauritania',
  'Indian/Mauritius': 'Mauritius',
  'Indian/Mayotte': 'Mayotte',
  'America/Mexico_City': 'Mexico',
  'Pacific/Chuuk': 'Micronesia, Federated States of',
  'Europe/Chisinau': 'Moldova, Republic of',
  'Europe/Monaco': 'Monaco',
  'Asia/Ulaanbaatar': 'Mongolia',
  'Europe/Podgorica': 'Montenegro',
  'America/Montserrat': 'Montserrat',
  'Africa/Casablanca': 'Morocco',
  'Africa/Maputo': 'Mozambique',
  'Asia/Yangon': 'Myanmar',
  'Africa/Windhoek': 'Namibia',
  'Pacific/Nauru': 'Nauru',
  'Asia/Kathmandu': 'Nepal',
  'Europe/Amsterdam': 'Netherlands',
  'Pacific/Noumea': 'New Caledonia',
  'Pacific/Auckland': 'New Zealand',
  'America/Managua': 'Nicaragua',
  'Africa/Niamey': 'Niger',
  'Africa/Lagos': 'Nigeria',
  'Pacific/Niue': 'Niue',
  'Pacific/Norfolk': 'Norfolk Island',
  'Pacific/Saipan': 'Northern Mariana Islands',
  'Europe/Oslo': 'Norway',
  'Asia/Muscat': 'Oman',
  'Asia/Karachi': 'Pakistan',
  'Pacific/Palau': 'Palau',
  'Asia/Gaza': 'Palestinian Territory, Occupied',
  'America/Panama': 'Panama',
  'Pacific/Port_Moresby': 'Papua New Guinea',
  'America/Asuncion': 'Paraguay',
  'America/Lima': 'Peru',
  'Asia/Manila': 'Philippines',
  'Pacific/Pitcairn': 'Pitcairn',
  'Europe/Warsaw': 'Poland',
  'Europe/Lisbon': 'Portugal',
  'America/Puerto_Rico': 'Puerto Rico',
  'Asia/Qatar': 'Qatar',
  'Indian/Reunion': 'Réunion',
  'Europe/Bucharest': 'Romania',
  'Europe/Moscow': 'Russia',
  'Africa/Kigali': 'Rwanda',
  'America/St_Barthelemy': 'Saint Barthélemy',
  'Atlantic/St_Helena': 'Saint Helena, Ascension and Tristan da Cunha',
  'America/St_Kitts': 'Saint Kitts and Nevis',
  'America/St_Lucia': 'Saint Lucia',
  'America/Marigot': 'Saint Martin (French part)',
  'America/Miquelon': 'Saint Pierre and Miquelon',
  'America/St_Vincent': 'Saint Vincent and the Grenadines',
  'Pacific/Apia': 'Samoa',
  'Europe/San_Marino': 'San Marino',
  'Africa/Sao_Tome': 'Sao Tome and Principe',
  'Asia/Riyadh': 'Saudi Arabia',
  'Africa/Dakar': 'Senegal',
  'Europe/Belgrade': 'Serbia',
  'Indian/Mahe': 'Seychelles',
  'Africa/Freetown': 'Sierra Leone',
  'Asia/Singapore': 'Singapore',
  'Europe/Bratislava': 'Slovakia',
  'Europe/Ljubljana': 'Slovenia',
  'Pacific/Guadalcanal': 'Solomon Islands',
  'Africa/Mogadishu': 'Somalia',
  'Africa/Johannesburg': 'South Africa',
  'Atlantic/South_Georgia': 'South Georgia and the South Sandwich Islands',
  'Africa/Juba': 'South Sudan',
  'Europe/Madrid': 'Spain',
  'Asia/Colombo': 'Sri Lanka',
  'Africa/Khartoum': 'Sudan',
  'America/Paramaribo': 'Suriname',
  'Africa/Mbabane': 'Swaziland',
  'Europe/Stockholm': 'Sweden',
  'Europe/Zurich': 'Switzerland',
  'Asia/Damascus': 'Syrian Arab Republic',
  'Asia/Taipei': 'Taiwan, Province of China',
  'Asia/Dushanbe': 'Tajikistan',
  'Africa/Dar_es_Salaam': 'Tanzania, United Republic of',
  'Asia/Bangkok': 'Thailand',
  'Asia/Dili': 'Timor-Leste',
  'Africa/Lome': 'Togo',
  'Pacific/Fakaofo': 'Tokelau',
  'Pacific/Tongatapu': 'Tonga',
  'America/Port_of_Spain': 'Trinidad and Tobago',
  'Africa/Tunis': 'Tunisia',
  'Europe/Istanbul': 'Turkey',
  'Asia/Ashgabat': 'Turkmenistan',
  'America/Grand_Turk': 'Turks and Caicos Islands',
  'Pacific/Funafuti': 'Tuvalu',
  'Africa/Kampala': 'Uganda',
  'Europe/Kiev': 'Ukraine',
  'Asia/Dubai': 'United Arab Emirates',
  'Europe/London': 'United Kingdom',
  'America/New_York': 'United States',
  'America/Montevideo': 'Uruguay',
  'Asia/Tashkent': 'Uzbekistan',
  'Pacific/Efate': 'Vanuatu',
  'America/Caracas': 'Venezuela, Bolivarian Republic of',
  'Asia/Ho_Chi_Minh': 'Viet Nam',
  'America/Tortola': 'Virgin Islands, British',
  'America/St_Thomas': 'Virgin Islands, U.S.',
  'Pacific/Wallis': 'Wallis and Futuna',
  'Africa/El_Aaiun': 'Western Sahara',
  'Asia/Aden': 'Yemen',
  'Africa/Lusaka': 'Zambia',
  'Africa/Harare': 'Zimbabwe',
};

/** Current IANA names that browsers may report for older names used in the table. */
const TIME_ZONE_ALIASES: Record<string, string> = {
  'Europe/Kyiv': 'Europe/Kiev',
  'America/Nuuk': 'America/Godthab',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Dacca': 'Asia/Dhaka',
  'Africa/Asmera': 'Africa/Asmara',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Pacific/Truk': 'Pacific/Chuuk',
};

export function getMyCountryName(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return COUNTRY_BY_TIME_ZONE[zone] ?? COUNTRY_BY_TIME_ZONE[TIME_ZONE_ALIASES[zone]] ?? DEFAULT_COUNTRY;
  } catch {
    return DEFAULT_COUNTRY;
  }
}
